import type { Doc } from "building-types/doc";
import { EdgeBehaviorType } from "building-types/edgeBehavior/edgeBehaviorTypesEnum";
import { FoundationSectionSizeType } from "building-types/foundations/foundationEnums";
import type { ComponentMemberEntityHandle } from "building-types/handles/docEntityHandleTypes";
import type { StairBlockHosting } from "building-types/stairs/stairTypes";
import { StairBlockType } from "building-types/stairs/stairTypesEnum";
import { assertUnreachable } from "common/utils/assertUnreachable";
import { batchMergeIn } from "common/utils/objectUtils";

/**
 * Doc entity ids grouped by type to check for if an entity is in the component selection
 */
export interface ComponentScopedMemberSets {
  readonly roomIds: ReadonlySet<string>;
  readonly stairSystemIds: ReadonlySet<string>;
  readonly railingIds: ReadonlySet<string>;
  readonly roofIds: ReadonlySet<string>;
  readonly roofSystemIds: ReadonlySet<string>;
  readonly foundationSystemIds: ReadonlySet<string>;
  readonly floorSystemIds: ReadonlySet<string>;
  readonly componentInstanceIds: ReadonlySet<string>;
  readonly optionSetIds: ReadonlySet<string>;
}

export function buildComponentScopedMemberSets(
  handles: readonly ComponentMemberEntityHandle[],
): ComponentScopedMemberSets {
  const roomIds = new Set<string>();
  const stairSystemIds = new Set<string>();
  const railingIds = new Set<string>();
  const roofIds = new Set<string>();
  const roofSystemIds = new Set<string>();
  const foundationSystemIds = new Set<string>();
  const floorSystemIds = new Set<string>();
  const componentInstanceIds = new Set<string>();
  const optionSetIds = new Set<string>();

  for (const h of handles) {
    switch (h.type) {
      case "ROOM":
        roomIds.add(h.id);
        break;
      case "STAIR_SYSTEM":
        stairSystemIds.add(h.id);
        break;
      case "RAILING":
        railingIds.add(h.id);
        break;
      case "ROOF":
        roofIds.add(h.id);
        break;
      case "ROOF_SYSTEM":
        roofSystemIds.add(h.id);
        break;
      case "FOUNDATION_SYSTEM":
        foundationSystemIds.add(h.id);
        break;
      case "FLOOR_SYSTEM":
        floorSystemIds.add(h.id);
        break;
      case "COMPONENT_INSTANCE":
        componentInstanceIds.add(h.id);
        break;
      case "OPTION_SET":
        optionSetIds.add(h.id);
        break;
      case "LEVEL":
      case "BLOCK":
        break;
      default:
        assertUnreachable(h);
    }
  }

  return {
    roomIds,
    stairSystemIds,
    railingIds,
    roofIds,
    roofSystemIds,
    foundationSystemIds,
    floorSystemIds,
    componentInstanceIds,
    optionSetIds,
  };
}

/**
 * If a datum is a level id itself or a generated id `levelId-<name>` from level datums, if the level was
 * remapped, remap it, otherwise clear it.
 * For other entity types, return the datum id unchanged.
 */
export function remapDatumIdForComponent(
  datumId: string | undefined,
  doc: Doc,
  oldToNewLevelId: Record<string, string>,
): string | undefined {
  if (datumId === undefined || datumId === "") {
    return datumId;
  }

  //If the datum is (or is a named height of) a remapped level, remap it
  const directNew = oldToNewLevelId[datumId];
  if (directNew !== undefined) {
    return directNew;
  }

  for (const oldLevelId of Object.keys(oldToNewLevelId)) {
    if (datumId.startsWith(`${oldLevelId}-`)) {
      return oldToNewLevelId[oldLevelId] + datumId.slice(oldLevelId.length);
    }
  }

  //If the datum is (or is a named height of) a level that was not remapped, clear it
  if (doc.levels[datumId] !== undefined) {
    return undefined;
  }

  for (const levelId of Object.keys(doc.levels)) {
    if (oldToNewLevelId[levelId] === undefined && datumId.startsWith(`${levelId}-`)) {
      return undefined;
    }
  }

  return datumId;
}

/**
 * Level ids referenced by a building datum id (bare level key or `levelId-<name>` generated datum).
 */
export function levelIdsReferencedByDatumId(doc: Doc, datumId: string | undefined): readonly string[] {
  if (datumId === undefined || datumId === "") {
    return [];
  }

  if (doc.levels[datumId] !== undefined) {
    return [datumId];
  }

  const levelIds: string[] = [];
  for (const levelId of Object.keys(doc.levels)) {
    if (datumId.startsWith(`${levelId}-`)) {
      levelIds.push(levelId);
    }
  }
  return levelIds;
}

function levelIdsFromStairBlockHosting(doc: Doc, hosting: StairBlockHosting | undefined): readonly string[] {
  if (hosting?.type !== "DATUM") {
    return [];
  }
  return levelIdsReferencedByDatumId(doc, hosting.datum);
}

/**
 * Levels referenced by vertical datums / DATUM hosting on stair blocks for the given stair systems.
 */
export function levelIdsFromStairBlockDatums(doc: Doc, stairSystemIds: ReadonlySet<string>): ReadonlySet<string> {
  const levelIds: string[] = [];

  for (const sb of Object.values(doc.stairBlocks)) {
    if (!stairSystemIds.has(sb.stairSystemId)) {
      continue;
    }

    switch (sb.type) {
      case StairBlockType.RUN: {
        levelIds.push(
          ...levelIdsReferencedByDatumId(doc, sb.datumVerticalUpper),
          ...levelIdsReferencedByDatumId(doc, sb.datumVerticalLower),
          ...levelIdsFromStairBlockHosting(doc, sb.hostingUpper),
          ...levelIdsFromStairBlockHosting(doc, sb.hostingLower),
        );
        break;
      }
      case StairBlockType.LANDING: {
        levelIds.push(
          ...levelIdsReferencedByDatumId(doc, sb.datumVertical),
          ...levelIdsFromStairBlockHosting(doc, sb.hosting),
        );
        break;
      }
      default:
        assertUnreachable(sb);
    }
  }

  return new Set(levelIds);
}

/**
 * If the datum is an entity that can be included in a component, return whether it is included; otherwise undefined (unknown / empty id).
 */
export function isDatumIdIncludedInComponent(
  datumId: string | undefined,
  doc: Doc,
  memberSets: ComponentScopedMemberSets,
): boolean | undefined {
  if (datumId === undefined || datumId === "") {
    return undefined;
  }

  if (doc.rooms[datumId] !== undefined) {
    return memberSets.roomIds.has(datumId);
  }

  const stairBlock = doc.stairBlocks[datumId];
  if (stairBlock !== undefined) {
    return memberSets.stairSystemIds.has(stairBlock.stairSystemId);
  }

  if (doc.railings[datumId] !== undefined) {
    return memberSets.railingIds.has(datumId);
  }

  const roof = doc.roofs[datumId];
  if (roof !== undefined) {
    if (memberSets.roofIds.has(datumId)) {
      return true;
    }
    if (roof.parentId !== undefined && memberSets.roofSystemIds.has(roof.parentId)) {
      return true;
    }
    return false;
  }

  if (doc.componentInstances[datumId] !== undefined) {
    return memberSets.componentInstanceIds.has(datumId);
  }

  if (doc.optionSets[datumId] !== undefined) {
    return memberSets.optionSetIds.has(datumId);
  }

  if (doc.foundationSystems[datumId] !== undefined) {
    return memberSets.foundationSystemIds.has(datumId);
  }

  if (doc.floorSystems[datumId] !== undefined) {
    return memberSets.floorSystemIds.has(datumId);
  }

  return undefined;
}

function remapOrFilterHostForComponent(
  datumId: string | undefined,
  doc: Doc,
  oldToNewLevelId: Record<string, string>,
  memberSets: ComponentScopedMemberSets,
  isStairBlockExcluded: boolean = false,
): string | undefined {
  const hostRemapped = remapDatumIdForComponent(datumId, doc, oldToNewLevelId);
  if (hostRemapped !== datumId) {
    return isStairBlockExcluded ? datumId : hostRemapped;
  }
  const hostIncluded = isDatumIdIncludedInComponent(datumId, doc, memberSets);
  return hostIncluded === isStairBlockExcluded ? undefined : datumId;
}

function remapStairBlockHosting(
  hosting: StairBlockHosting | undefined,
  doc: Doc,
  oldToNewLevelId: Record<string, string>,
  memberSets: ComponentScopedMemberSets,
  isStairBlockExcluded: boolean = false,
): StairBlockHosting | undefined {
  if (!hosting?.type) {
    return hosting;
  }
  switch (hosting.type) {
    case "EDGE": {
      if (isStairBlockExcluded !== isDatumIdIncludedInComponent(hosting.hostId, doc, memberSets)) {
        return hosting;
      }
      return {
        ...hosting,
        hostId: undefined,
        hostEdgeId: undefined,
      };
    }
    case "DATUM": {
      const nextDatum = remapOrFilterHostForComponent(
        hosting.datum,
        doc,
        oldToNewLevelId,
        memberSets,
        isStairBlockExcluded,
      );
      if (nextDatum === hosting.datum) {
        return hosting;
      }
      return {
        ...hosting,
        datum: nextDatum,
      };
    }
    default:
      assertUnreachable(hosting);
  }
}

/**
 * Remap or clears references, for when a stair block or the entities it references are moved to a component
 */
export function docRemapStairBlockRefsForComponent(
  doc: Doc,
  oldToNewLevelId: Record<string, string>,
  addedHandles: readonly ComponentMemberEntityHandle[],
): Doc {
  const systemIdSet = new Set(addedHandles.filter((h) => h.type === "STAIR_SYSTEM").map((h) => h.id));
  const memberSets = buildComponentScopedMemberSets(addedHandles);
  const pathValuePairs: [(string | number)[], Record<string, unknown>][] = [];

  for (const sb of Object.values(doc.stairBlocks)) {
    const isStairBlockExcluded = !systemIdSet.has(sb.stairSystemId);

    const remapDatum = (datumId: string | undefined) =>
      remapOrFilterHostForComponent(datumId, doc, oldToNewLevelId, memberSets, isStairBlockExcluded);
    const remapHosting = (hosting: StairBlockHosting | undefined) =>
      remapStairBlockHosting(hosting, doc, oldToNewLevelId, memberSets, isStairBlockExcluded);

    switch (sb.type) {
      case StairBlockType.RUN: {
        pathValuePairs.push([
          ["stairBlocks", sb.id],
          {
            datumVerticalUpper: remapDatum(sb.datumVerticalUpper),
            datumVerticalLower: remapDatum(sb.datumVerticalLower),
            hostingUpper: remapHosting(sb.hostingUpper),
            hostingLower: remapHosting(sb.hostingLower),
          },
        ]);
        break;
      }
      case StairBlockType.LANDING: {
        pathValuePairs.push([
          ["stairBlocks", sb.id],
          {
            datumVertical: remapDatum(sb.datumVertical),
            hosting: remapHosting(sb.hosting),
          },
        ]);
        break;
      }
      default:
        assertUnreachable(sb);
    }
  }

  // Remap foundationLevelId on included foundation patterns to a level in the component
  for (const edgeBehavior of Object.values(doc.edgeBehaviors ?? {})) {
    if (!edgeBehavior || edgeBehavior.edgeBehaviorType !== EdgeBehaviorType.FOUNDATION_PATTERN) {
      continue;
    }
    const { foundationPattern, parentId, id } = edgeBehavior;
    if (foundationPattern.patternType !== FoundationSectionSizeType.ISOLATED) {
      continue;
    }

    const stairBlock = doc.stairBlocks[parentId];
    const isParentIncluded =
      (stairBlock && memberSets.stairSystemIds.has(stairBlock.stairSystemId)) || memberSets.roomIds.has(parentId);
    if (!isParentIncluded) {
      continue;
    }

    let nextFoundationLevelId = remapDatumIdForComponent(foundationPattern.foundationLevelId, doc, oldToNewLevelId);

    // Adding to an existing component uses an empty level map.
    // Set the level to the host's new level (already reassigned) to maintain a valid foundation level.
    if (nextFoundationLevelId === undefined) {
      if (stairBlock) {
        nextFoundationLevelId = doc.stairSystems[stairBlock.stairSystemId]?.levelId;
      } else {
        const blockId = doc.rooms[parentId]?.parentId;
        nextFoundationLevelId = blockId ? doc.blocks[blockId]?.levelId : undefined;
      }
    }

    pathValuePairs.push([
      ["edgeBehaviors", id],
      {
        foundationPattern: {
          ...foundationPattern,
          foundationLevelId: nextFoundationLevelId,
        },
      },
    ]);
  }
  return batchMergeIn(doc, pathValuePairs);
}
